import {
  Appointment,
  GetSlotsForDayResponse,
  SchedulableResourceType,
  TokenSlot,
} from "@/types/scheduling/schedule";
import { format, isWithinInterval } from "date-fns";
import { useCallback, useEffect, useMemo } from "react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { groupSlotsByAvailability } from "@/pages/Appointments/utils";
import scheduleApi from "@/types/scheduling/scheduleApi";
import query from "@/Utils/request/query";
import { dateQueryString } from "@/Utils/utils";
import { useQuery } from "@tanstack/react-query";
import { ClipboardCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

interface AppointmentSlotPickerProps {
  facilityId: string;
  resourceId?: string;
  onSlotSelect: (slotId: string | undefined) => void;
  selectedSlotId?: string;
  onSlotDetailsChange?: (slot: TokenSlot) => void;
  currentAppointment?: Appointment;
  selectedDate: Date;
  resourceType: SchedulableResourceType;
  pinnedSlot?: TokenSlot | null;
  reservedUntil?: Date | null;
  disabled?: boolean;
}

export function AppointmentSlotPicker({
  facilityId,
  resourceId,
  onSlotSelect,
  selectedSlotId,
  onSlotDetailsChange,
  currentAppointment,
  selectedDate,
  resourceType,
  pinnedSlot,
  reservedUntil,
  disabled = false,
}: AppointmentSlotPickerProps) {
  const { t } = useTranslation();

  const slotsQuery = useQuery({
    queryKey: ["slots", facilityId, resourceId, dateQueryString(selectedDate)],
    queryFn: query(scheduleApi.slots.getSlotsForDay, {
      pathParams: { facilityId },
      body: {
        resource_type: resourceType,
        resource_id: resourceId ?? "",
        day: dateQueryString(selectedDate),
      },
    }),
    enabled: !!resourceId && !!selectedDate,
    select: (data: GetSlotsForDayResponse) => {
      if (currentAppointment) {
        return data.results.filter(
          (slot) => slot.id !== currentAppointment.token_slot.id,
        );
      }
      return data.results;
    },
  });

  // Update slot details when a slot is selected
  const handleSlotSelect = useCallback(
    (slotId: string | undefined) => {
      onSlotSelect(slotId);
      if (slotId && onSlotDetailsChange) {
        const allSlots = slotsQuery.data || [];
        const selectedSlot = allSlots.find((slot) => slot.id === slotId);

        if (selectedSlot) {
          onSlotDetailsChange(selectedSlot);
        }
      }
    },
    [onSlotSelect, onSlotDetailsChange, slotsQuery.data],
  );

  const { slotGroups, availableSlots, displaySlots } = useMemo(() => {
    const allSlots = [...(slotsQuery.data || [])];
    // Inject pinned slot if it's not in the API results (it's fully allocated
    // because the pending appointment holds it, but we still show it as reserved).
    if (pinnedSlot && !allSlots.find((s) => s.id === pinnedSlot.id)) {
      allSlots.push(pinnedSlot);
    }
    const slotGroups = groupSlotsByAvailability(allSlots);
    // Exclude the pinned/reserved slot from auto-selection candidates.
    const availableSlots = slotGroups
      .flatMap((group) => group.slots)
      .filter((s) => !pinnedSlot || s.id !== pinnedSlot.id);
    return { slotGroups, availableSlots, displaySlots: allSlots };
  }, [slotsQuery.data, pinnedSlot]);

  // Pre-select the first slot for current date if there are any slots available.
  // Skip auto-selection when a pinned slot exists (pending booking restoration)
  // to avoid triggering a spurious cancel of the pending booking.
  useEffect(() => {
    if (pinnedSlot) return;

    const currentSelectedSlot = displaySlots.find(
      (slot) => slot.id === selectedSlotId,
    );

    if (currentSelectedSlot) {
      onSlotDetailsChange?.(currentSelectedSlot);
      return;
    }

    const nextSlot = availableSlots[0];
    if (!nextSlot && selectedSlotId) {
      onSlotSelect(undefined);
      return;
    }

    if (!nextSlot || nextSlot.id === selectedSlotId) return;

    handleSlotSelect(nextSlot.id);
  }, [
    availableSlots,
    displaySlots,
    handleSlotSelect,
    onSlotDetailsChange,
    onSlotSelect,
    pinnedSlot,
    selectedSlotId,
  ]);

  return (
    <div
      className={cn(
        "sm:flex flex-col gap-3 w-full overflow-y-auto",
        (!resourceId || disabled) && "opacity-50 pointer-events-none",
      )}
    >
      <div className="justify-between hidden sm:flex  gap-2 items-center">
        <span className="font-semibold text-gray-950 text-base dark:text-gray-50">
          {format(selectedDate, "MMMM d yyyy")}
        </span>
        {!!slotsQuery.data?.length && (
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {availableSlots.length} {t("available_time_slots")}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2 sm:hidden">
        <span className="font-semibold text-lg text-gray-950 mb-2 dark:text-gray-50">
          {format(selectedDate, "MMMM d yyyy")}
        </span>
        <div className="mb-2">
          {!!slotsQuery.data?.length && (
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {availableSlots.length} {t("available_time_slots")}
            </span>
          )}
        </div>
      </div>
      <div className="border-b border-gray-200 w-full" />
      {slotsQuery.isFetching ? (
        <div className="flex flex-wrap gap-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-20" />
          ))}
        </div>
      ) : (
        <div>
          {slotsQuery.data == null && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-3">
                <div className="w-32 h-4 bg-gray-50 rounded" />
                <div className="grid grid-cols-4 gap-2">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-12 bg-gray-50 rounded flex text-gray-400 items-center justify-center"
                    >
                      --:--
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="w-32 h-4 bg-gray-50 rounded" />
                <div className="grid grid-cols-4 gap-4">
                  {Array.from({ length: 12 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-12 bg-gray-50 rounded flex text-gray-400 items-center justify-center"
                    >
                      --:--
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {slotsQuery.data?.length === 0 && (
            <div className="flex items-center justify-center py-32 border-2 border-gray-200 border-dashed rounded-lg text-center">
              <p className="text-gray-400">
                {t("no_slots_available_for_this_date")}
              </p>
            </div>
          )}
          {!!slotsQuery.data?.length &&
            slotGroups.map(({ availability, slots }) => (
              <div key={availability.name} className="flex flex-col">
                <div className="flex flex-row gap-2 items-center mb-2 mt-2 sm:mt-0">
                  <ClipboardCheck size={16} />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {availability.name}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 xl:grid-cols-3 2xl:grid-cols-5 gap-2">
                  {slots.map((slot) => (
                    <TokenSlotButton
                      key={slot.id}
                      slot={slot}
                      availability={availability}
                      selectedSlotId={selectedSlotId}
                      isReserved={!!pinnedSlot && slot.id === pinnedSlot.id}
                      reservedUntil={
                        pinnedSlot && slot.id === pinnedSlot.id
                          ? reservedUntil
                          : null
                      }
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) {
                          return;
                        }
                        handleSlotSelect(
                          selectedSlotId === slot.id ? undefined : slot.id,
                        );
                      }}
                    />
                  ))}
                </div>
                <Separator className="my-6" />
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

export const TokenSlotButton = ({
  slot,
  availability,
  selectedSlotId,
  onClick,
  className,
  isReserved,
  reservedUntil,
  disabled,
}: {
  slot: Omit<TokenSlot, "availability">;
  availability: TokenSlot["availability"];
  selectedSlotId: string | undefined;
  onClick: () => void;
  className?: string;
  isReserved?: boolean;
  reservedUntil?: Date | null;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();

  const percentage = slot.allocated / availability.tokens_per_slot;

  const isOngoingSlot = isWithinInterval(new Date(), {
    start: slot.start_datetime,
    end: slot.end_datetime,
  });

  const secsLeft =
    isReserved && reservedUntil
      ? Math.max(0, Math.floor((reservedUntil.getTime() - Date.now()) / 1000))
      : null;
  const timerLabel =
    secsLeft !== null
      ? `${String(Math.floor(secsLeft / 60)).padStart(2, "0")}:${String(secsLeft % 60).padStart(2, "0")}`
      : null;

  return (
    <Button
      key={slot.id}
      size="lg"
      type="button"
      variant={
        isReserved
          ? "outline"
          : selectedSlotId === slot.id
            ? "primary"
            : "outline"
      }
      onClick={onClick}
      disabled={
        disabled ||
        (!isReserved && slot.allocated === availability.tokens_per_slot)
      }
      className={cn(
        "flex flex-col items-center group gap-0 w- relative",
        isReserved && "border-amber-400 bg-amber-50 hover:bg-amber-100",
        disabled && "cursor-not-allowed",
        className,
      )}
    >
      <span className="font-semibold">
        {format(slot.start_datetime, "HH:mm")}
      </span>
      {isReserved ? (
        <span className="text-xs text-amber-600 font-medium">
          {timerLabel ? `${t("reserved")} ${timerLabel}` : t("reserved")}
        </span>
      ) : (
        <span
          className={cn(
            "text-xs group-hover:text-inherit",
            percentage >= 1
              ? "text-gray-400"
              : percentage >= 0.8
                ? "text-red-600"
                : percentage >= 0.6
                  ? "text-yellow-600"
                  : "text-green-600",
            selectedSlotId === slot.id && "text-white",
          )}
        >
          {isOngoingSlot ? (
            <>
              {t("live")} •{" "}
              {t("tokens_left", {
                count: availability.tokens_per_slot - slot.allocated,
              })}
            </>
          ) : (
            t("tokens_left", {
              count: availability.tokens_per_slot - slot.allocated,
            })
          )}
        </span>
      )}
    </Button>
  );
};
